/**
 * Behaviour for the cart add-on cards (upsell strip + recommendations panel):
 * the hover zoom preview and the greeting-card message prompt.
 *
 * WHY THIS IS A REAL ASSET AND NOT A {% javascript %} BLOCK
 *
 * This code used to live in a {% javascript %} tag inside
 * snippets/cart-addon-card.liquid. Shopify concatenates those into
 * snippet-scripts.js and injects the tag through content_for_header — but only
 * for snippets rendered in the INITIAL document.
 *
 * cart-upsells renders nothing when every upsell product is already in the
 * cart, and the recommendations panel can be empty too. On those page loads no
 * add-on card renders, so Shopify never emits snippet-scripts.js. Add or remove
 * something afterwards, the drawer re-renders through the Section Rendering
 * API, a <greeting-card-prompt> appears in the DOM — and the file that defines
 * it was never on the page. The element stays an inert HTMLElement: no
 * listener, no console error, and the button still shows its :active highlight
 * because that is pure CSS.
 *
 * Confirmed on a broken session:
 *   everFetched:  [scripts.js]        <- no snippet-scripts.js
 *   constructors: [HTMLElement, ...]  <- never upgraded
 *
 * Other add-on cards were unaffected because their Add button behaviour comes
 * from the theme's always-loaded modules, not from the snippet bundle.
 *
 * Loaded from snippets/cart-drawer.liquid, which the layout renders on every
 * page, so the definitions are always present regardless of what the drawer
 * happens to contain at first paint.
 */

class CartAddonZoom extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('a[data-zoom-src]');
    if (!this.link) return;
    // The zoom preview is a hover affordance. On iOS a touch fires
    // pointerenter, but the matching pointerleave is unreliable (lost when
    // the touch becomes a carousel scroll -> pointercancel, or when the
    // card is morphed away after an add), leaving the overlay stuck on
    // screen. Only arm it on devices that actually hover.
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.addEventListener('pointerenter', this.show);
    this.addEventListener('pointerleave', this.hide);
    this.addEventListener('pointercancel', this.hide);
    this.addEventListener('focusin', this.show);
    this.addEventListener('focusout', this.hide);
  }

  disconnectedCallback() {
    // Cart updates morph/prune cards while the pointer may still be over
    // one; if this element is removed before pointerleave fires, hide the
    // shared overlay so it can't linger after the drawer closes.
    this.hide();
  }

  show = (event) => {
    // Belt and braces: never show for touch/pen pointers even if the
    // hover media query matched (e.g. hybrid touchscreen laptops).
    if (event && event.pointerType && event.pointerType !== 'mouse') return;
    const src = this.link.dataset.zoomSrc;
    if (!src) return;
    const overlay = CartAddonZoom.overlay();
    const size = 160;
    const gap = 8;
    const offset = 28;
    const rect = this.link.getBoundingClientRect();

    let top = rect.top - size + offset;
    top = Math.max(gap, Math.min(top, window.innerHeight - size - gap));

    let left = rect.right - offset;
    left = Math.max(gap, Math.min(left, window.innerWidth - size - gap));

    overlay.style.top = `${top}px`;
    overlay.style.left = `${left}px`;
    overlay.querySelector('img').src = src;
    overlay.style.display = 'block';
  };

  hide = () => {
    // Don't lazily create the overlay just to hide it (disconnectedCallback
    // runs on every drawer re-render, including on touch devices).
    if (CartAddonZoom._overlay) CartAddonZoom._overlay.style.display = 'none';
  };

  static overlay() {
    if (!CartAddonZoom._overlay) {
      const el = document.createElement('div');
      el.style.cssText = [
        'position:fixed',
        'z-index:999',
        'width:160px',
        'height:160px',
        'overflow:hidden',
        'background:var(--color-background, #fff)',
        'border:1px solid rgba(0,0,0,0.1)',
        'box-shadow:0 8px 24px rgba(0,0,0,0.18)',
        'pointer-events:none',
        'display:none',
      ].join(';');
      const img = document.createElement('img');
      img.alt = '';
      img.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block';
      el.appendChild(img);
      document.body.appendChild(el);
      CartAddonZoom._overlay = el;
    }
    return CartAddonZoom._overlay;
  }
}

if (!customElements.get('cart-addon-zoom')) {
  customElements.define('cart-addon-zoom', CartAddonZoom);
}

/**
 * Greeting card message prompt: the Add button opens a small dialog asking
 * for the message before submitting the card's product form. The textarea
 * lives inside the form, so the native product-form-component submit flow
 * picks the property up unchanged.
 *
 * EVERYTHING IS RESOLVED AT CLICK TIME, DELIBERATELY.
 *
 * A previous version cached this.opener / this.dialog in connectedCallback
 * and bailed out with `if (!this.opener || !this.dialog) return;` when they
 * were missing. That is a silent, permanent failure: the element stays in
 * the DOM looking normal, the button still shows its :active highlight on
 * tap because that is pure CSS, and nothing is ever logged.
 *
 * It fires because the upsell strip is rebuilt constantly — cart-upsells
 * skips any product already in the cart, so slides are created and removed
 * on every cart change. When the morph inserts a <greeting-card-prompt>
 * before its children are attached, connectedCallback runs against an empty
 * subtree and that card's button is dead for the rest of the session.
 * Intermittent by nature, and worse on mobile where the drawer re-renders
 * more often.
 *
 * Delegating on the host element removes the ordering dependency entirely:
 * the listener lives on <greeting-card-prompt> itself, and the children are
 * looked up when the click actually happens, by which point they exist.
 */
class GreetingCardPrompt extends HTMLElement {
  #bound = false;

  connectedCallback() {
    // Survives re-connection (morph can move the node between parents).
    if (this.#bound) return;
    this.#bound = true;

    this.addEventListener('click', this.#handleClick);
  }

  get #dialog() {
    return this.querySelector('.greeting-card-prompt__dialog');
  }

  get #textarea() {
    return this.querySelector('.greeting-card-prompt__textarea');
  }

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const dialog = this.#dialog;
    if (!dialog) return;

    if (target.closest('.greeting-card-prompt__open')) {
      if (!dialog.open) {
        // show() rather than nothing if showModal is unavailable or refuses
        // (nested-modal edge cases): a visible dialog beats a dead button.
        try {
          dialog.showModal();
        } catch (error) {
          try {
            dialog.show();
          } catch (fallbackError) {
            return;
          }
        }
      }
      this.#textarea?.focus();
      this.#watchSubmit();
      return;
    }

    if (target.closest('.greeting-card-prompt__cancel')) {
      dialog.close();
      return;
    }

    // Click on the backdrop closes the dialog
    if (event.target === dialog) dialog.close();
  };

  /**
   * The add continues via the native product-form flow; close the dialog and
   * clear the message so a later re-add starts fresh. Bound when the dialog
   * opens rather than at connect time, so it always finds the current form.
   */
  #watchSubmit() {
    const form = this.closest('form');
    if (!form) return;

    form.addEventListener(
      'submit',
      () => {
        this.#dialog?.close();
        // Clear after the current task so the form data (including the
        // message) has already been captured by the submit handler.
        setTimeout(() => {
          const textarea = this.#textarea;
          if (textarea) textarea.value = '';
        }, 0);
      },
      { once: true }
    );
  }
}

if (!customElements.get('greeting-card-prompt')) {
  customElements.define('greeting-card-prompt', GreetingCardPrompt);
}
